/**
 * Normalises a ScheduleInterviewPayload into a flat ChangeSnapshot
 * used for diffing original vs current state when editing an interview.
 *
 * Pure function — no hooks, no side effects.
 */
import type { EmailCommConfigPayload } from '@/api/useJobStageActions/useCreateJobStageAction';
import type { ScheduleInterviewPayload } from '../config/types';

/**
 * The API-hydrated shape (fromEditApi.ts) uses backend key names:
 *   from_user_id / cc_user_ids / bcc_user_ids
 * The form-synced shape (emailCompositionToCommConfig) uses frontend key names:
 *   from / cc / bcc
 * Both can appear in payloadRef.communication at different points in the
 * lifecycle, so normalizers must handle either variant.
 */
type EmailConfigShape = EmailCommConfigPayload & {
  from_user_id?: number;
  cc_user_ids?: number[];
  bcc_user_ids?: number[];
};

export interface ChangeSnapshot {
  // Details fields
  interviewName: unknown;
  interview_type: unknown;
  duration: unknown;
  start_time: unknown;
  end_time: unknown;
  location: unknown;
  calendar_visibility: unknown;
  enable_note_taker: unknown;
  participant_ids: string[];
  host_id: unknown;
  meeting_room_ids: string[];
  candidate_phone: unknown;
  /** 'edit' iff the user has opened the inline phone-edit pencil. When 'view'
   *  the form may sync candidate_phone in lazily — diffs on the phone field
   *  in that state are hydration noise, not a user edit. */
  isCandidatePhoneEditing: unknown;
  // Calendar invite bodies
  candidate_calendar_body: unknown;
  interviewer_calendar_body: unknown;
  // Candidate email — all fields trigger resend_email_to_candidate
  candidate_email_enabled: unknown;
  candidate_ics_enabled: unknown;
  candidate_email_from: string | null;
  candidate_email_cc: string[];
  candidate_email_bcc: string[];
  candidate_email_subject: unknown;
  candidate_email_body: unknown;
  candidate_email_attachments: string[];
  // Interviewer email — all fields trigger resend_email_to_interviewer
  interviewer_email_enabled: unknown;
  interviewer_ics_enabled: unknown;
  interviewer_email_from: string | null;
  interviewer_email_cc: string[];
  interviewer_email_bcc: string[];
  interviewer_email_subject: unknown;
  interviewer_email_body: unknown;
  interviewer_email_attachments: string[];
}

/** Normalise IDs to a sorted string array regardless of whether the form
 *  stored a scalar (one_on_one) or array (panel / self-schedule). */
function normalizeIds(val: unknown): string[] {
  if (Array.isArray(val)) return val.map(String).sort();
  if (typeof val === 'string' && val) return [val];
  return [];
}

/** Sender: handles both hydrated key (from_user_id) and synced key (from). */
function normalizeFrom(cfg: EmailConfigShape | undefined): string | null {
  if (!cfg) return null;
  const id = cfg.from ?? cfg.from_user_id;
  if (typeof id === 'number') return String(id);
  return null;
}

/** Recipients: handles both hydrated keys (cc_user_ids/bcc_user_ids) and synced keys (cc/bcc). */
function normalizeRecipients(
  synced: number[] | undefined,
  hydrated: number[] | undefined
): string[] {
  const arr = synced ?? hydrated;
  if (!arr || !arr.length) return [];
  return arr.map(String).sort();
}

/**
 * Normalize HTML body fields so the rich-text editor's first-render transforms
 * don't show up as user edits. The editor mounts AFTER `prefillVersion>0`
 * (i.e. after snapshot capture) and applies transforms beyond simple text
 * tweaks — it unwraps invalid block nesting (e.g. `<div>` inside `<p>`),
 * redistributes content across paragraphs, drops attributes its schema
 * doesn't recognise (e.g. `height` on `<img>`), and reshuffles `&nbsp;`
 * blank-paragraph wrappers.
 *
 * Reduce both bodies to a canonical form that captures user-meaningful
 * changes (text, image src, link href, attachment count) but ignores HTML
 * structural noise. This means a formatting-only edit (bold/italic
 * applied to existing text) won't trigger a resend — acceptable
 * trade-off; prevents false-positive resends that scared recruiters
 * before this lookup.
 */
function normalizeHtmlBody(value: unknown): unknown {
  if (typeof value !== 'string' || !value) return value;
  let s = value;
  s = s.replace(/\s+dir=(["'])auto\1/gi, '');
  s = s.replace(/\s+rel=(["'])noopener\s+noreferrer\s+nofollow\1/gi, '');
  s = s.replace(/\s+/g, ' ').trim();
  if (!/^</.test(s)) {
    s = `<p>${s}</p>`;
  }
  return canonicalizeHtmlForDiff(s);
}

/**
 * Reduce HTML to a canonical structure-insensitive form. Walks the parsed
 * DOM and emits a flat sequence of leaves that captures every signal a
 * recruiter editing the email/invite would care about — text content,
 * image src/alt, link href, heading level, list/table structure, line
 * breaks, embedded media — while ignoring shuffles the editor performs
 * on first mount: `<div>` unwrapping, `<p>` redistribution, wrapper
 * `<span>` insertion, dropped attributes (e.g. `height` on `<img>`),
 * empty `<p>&nbsp;</p>` blank-line markers, attribute order, and
 * inline formatting wrappers (`<strong>`, `<em>`, etc.) where the text
 * content already round-trips through the leaf sequence.
 *
 * Tag classification:
 *   - LEAF (emit record, no recurse): img, iframe, video, audio,
 *     source, br, hr
 *   - MARKER (emit record, then recurse): a (with href), ul, ol, table,
 *     tr, td, th, h1–h6, li, blockquote, pre, code
 *   - TRANSPARENT (recurse only): everything else (p, div, span, strong,
 *     em, etc.)
 *
 * Consecutive text leaves are merged after the walk so wrapping existing
 * text in `<strong>` (which produces `[text:'a', text:'b']` when the
 * formatter splits the run) collapses back to a single text leaf.
 */
function canonicalizeHtmlForDiff(html: string): string {
  if (typeof DOMParser === 'undefined') return html;
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(html, 'text/html');
  } catch {
    return html;
  }
  const leaves: CanonicalLeaf[] = [];
  walkCanonical(doc.body, leaves);
  return JSON.stringify(mergeConsecutiveText(leaves));
}

type CanonicalLeaf =
  | { k: 'text'; v: string }
  | { k: 'h'; lvl: number }
  | { k: 'li' }
  | { k: 'cell' }
  | { k: 'blockquote' }
  | { k: 'code' }
  | { k: 'ul' }
  | { k: 'ol' }
  | { k: 'table' }
  | { k: 'tr' }
  | { k: 'a'; href: string }
  | { k: 'img'; src: string; alt: string }
  | { k: 'media'; tag: string; src: string }
  | { k: 'br' }
  | { k: 'hr' };

const LEAF_MEDIA_TAGS = new Set([
  'iframe',
  'video',
  'audio',
  'source',
  'embed',
]);
const HEADING_REGEX = /^h([1-6])$/;

/**
 * Walk the parsed DOM in document order and emit a flat sequence of
 * canonical "leaves" into `out`. Three element classes keep the diff
 * structure-insensitive:
 *
 *   - LEAF (no recurse) — `img`, `iframe`, `video`, `audio`, `source`,
 *     `embed`, `br`, `hr`. The element IS its own content; emit a
 *     single record and skip its children.
 *
 *   - MARKER (emit + recurse) — `a`, `h1`-`h6`, `ul`, `ol`, `table`,
 *     `tr`, `td`/`th`, `li`, `blockquote`, `pre`/`code`. The marker
 *     captures the element's semantic identity (heading level, list
 *     ordering, link href, table cell, etc.) while the recursion
 *     captures whatever the user put inside.
 *
 *   - TRANSPARENT (recurse only) — `p`, `div`, `span`, inline
 *     formatting (`strong`, `em`, `b`, `i`, `u`, `s`, `del`, `mark`,
 *     `sub`, `sup`, `small`, `font`), structural sectioning, and any
 *     unknown tag. These are layout shells the editor mints freely or
 *     formatting wrappers whose contained text we already capture via
 *     recursion. Treating them transparently is what lets the diff
 *     ignore the editor's first-mount HTML reshuffling.
 *
 * Whitespace-only text nodes are dropped. A `<p>` that contains only
 * whitespace, `&nbsp;`, and/or a single `<br>` is a blank-line marker
 * and is skipped entirely (different serializers emit `<p></p>`,
 * `<p>&nbsp;</p>`, or `<p><br></p>` for the same blank line).
 */
function walkCanonical(node: Node, out: CanonicalLeaf[]): void {
  const NODE_TEXT = 3;
  const NODE_ELEMENT = 1;
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === NODE_TEXT) {
      const v = normalizeWhitespace(child.textContent ?? '');
      if (v) out.push({ k: 'text', v });
      continue;
    }
    if (child.nodeType !== NODE_ELEMENT) continue;
    const el = child as Element;
    const tag = el.tagName.toLowerCase();

    // Leaves (no recurse).
    if (tag === 'img') {
      out.push({
        k: 'img',
        src: el.getAttribute('src') ?? '',
        alt: el.getAttribute('alt') ?? '',
      });
      continue;
    }
    if (LEAF_MEDIA_TAGS.has(tag)) {
      out.push({ k: 'media', tag, src: el.getAttribute('src') ?? '' });
      continue;
    }
    if (tag === 'br') {
      out.push({ k: 'br' });
      continue;
    }
    if (tag === 'hr') {
      out.push({ k: 'hr' });
      continue;
    }

    // Markers (emit + recurse).
    const heading = HEADING_REGEX.exec(tag);
    if (heading) {
      out.push({ k: 'h', lvl: Number(heading[1]) });
      walkCanonical(el, out);
      continue;
    }
    if (tag === 'a') {
      out.push({ k: 'a', href: el.getAttribute('href') ?? '' });
      walkCanonical(el, out);
      continue;
    }
    if (
      tag === 'ul' ||
      tag === 'ol' ||
      tag === 'table' ||
      tag === 'tr' ||
      tag === 'li' ||
      tag === 'blockquote'
    ) {
      out.push({ k: tag });
      walkCanonical(el, out);
      continue;
    }
    // `<pre>` and `<code>` collapse to the same `code` leaf —
    // semantically close enough that swapping one for the other isn't
    // a content edit, and keeping a single leaf kind narrows the union.
    if (tag === 'pre' || tag === 'code') {
      out.push({ k: 'code' });
      walkCanonical(el, out);
      continue;
    }
    if (tag === 'td' || tag === 'th') {
      out.push({ k: 'cell' });
      walkCanonical(el, out);
      continue;
    }

    // Skip blank-line markers entirely. A `<p>` whose only content is
    // whitespace, &nbsp;, and/or a single <br> is the editor's way of
    // representing an empty line — different sources serialize blanks
    // differently (`<p></p>`, `<p>&nbsp;</p>`, `<p><br></p>`) and we
    // don't want any of those variants to count as a content edit.
    if (tag === 'p' && isBlankParagraph(el)) {
      continue;
    }

    // Transparent (recurse only) — p, div, span, strong, em, b, i, u, s,
    // del, mark, sub, sup, small, font, section, article, header,
    // footer, main, aside, nav, and any unknown tag.
    walkCanonical(el, out);
  }
}

function isBlankParagraph(el: Element): boolean {
  for (const child of Array.from(el.childNodes)) {
    if (child.nodeType === 3) {
      const v = normalizeWhitespace(child.textContent ?? '');
      if (v) return false;
      continue;
    }
    if (child.nodeType !== 1) continue;
    const childTag = (child as Element).tagName.toLowerCase();
    if (childTag !== 'br') return false;
  }
  return true;
}

function normalizeWhitespace(s: string): string {
  return s
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Collapse adjacent text leaves so wrapping part of a run in `<strong>`
 * (which causes the walker to emit `[text:'foo', text:'bar']`) reduces
 * to the same single text leaf as the unformatted version.
 */
function mergeConsecutiveText(leaves: CanonicalLeaf[]): CanonicalLeaf[] {
  const out: CanonicalLeaf[] = [];
  for (const leaf of leaves) {
    const prev = out[out.length - 1];
    if (leaf.k === 'text' && prev && prev.k === 'text') {
      prev.v = normalizeWhitespace(`${prev.v} ${leaf.v}`);
    } else {
      out.push(leaf);
    }
  }
  return out;
}

/** Normalise attachments: existing IDs (numbers) and new Files (by name+size). */
function normalizeAttachments(
  val: EmailCommConfigPayload['attachments']
): string[] {
  if (!val || !val.length) return [];
  return val
    .map((a) => {
      if (typeof a === 'number') return String(a);
      return `file:${a.name}:${a.size}`;
    })
    .sort();
}

export function buildChangeSnapshot(
  payload: ScheduleInterviewPayload
): ChangeSnapshot {
  const d = payload.details;
  const comm = payload.communication as Record<string, unknown>;

  const candCal = comm.calendar_invite_candidate as
    | { body?: unknown }
    | undefined;
  const intCal = comm.calendar_invite_interviewer as
    | { body?: unknown }
    | undefined;
  const candEmail = comm.candidate_email_comm_config as
    | EmailConfigShape
    | undefined;
  const intEmail = comm.interviewer_email_comm_config as
    | EmailConfigShape
    | undefined;

  // Use bookedSlot (set by hydrateDetailsFromApi from the API ISO datetime)
  // instead of startTime/endTime which are form-display strings only set
  // after the Details step syncs — bookedSlot is stable across both snapshots.
  const bookedSlot = d.bookedSlot as
    | { start?: string; end?: string }
    | undefined;

  // Defensive normalizers used below — every boolean / string field on the
  // snapshot funnels through these so a hydrated value (`false`, `''`, `0`)
  // and a form-cleared value (`null` / `undefined`) compare equal. Conditional
  // form fields (note-taker, host, etc.) clear to undefined when their
  // condition is false; without this, an unedited interview reports phantom
  // diffs every save.
  const normalizeString = (val: unknown): string =>
    typeof val === 'string' ? val.trim() : '';

  const snapshot: ChangeSnapshot = {
    interviewName: normalizeString(d.interviewName),
    interview_type: d.interview_type ?? null,
    duration:
      typeof d.duration === 'number' ? d.duration : Number(d.duration) || null,
    start_time: bookedSlot?.start ?? null,
    end_time: bookedSlot?.end ?? null,
    location: d.location ?? null,
    calendar_visibility: Boolean(d.calendar_visibility),
    // Conditional field: hidden for non-virtual locations (onsite/phone/hr).
    // Boolean()-collapse so `false` / `null` / `undefined` all read as false.
    enable_note_taker: Boolean(d.enable_note_taker),
    // Normalised — original is always string[], form may return scalar string
    participant_ids: normalizeIds(d.participant_ids),
    host_id: d.host_id || null,
    candidate_phone: d.candidate_phone ?? null,
    isCandidatePhoneEditing: d.isCandidatePhoneEditing ?? null,
    // Normalised — original is string[], form single-select may return scalar
    meeting_room_ids: normalizeIds(d.meeting_room_ids),
    candidate_calendar_body: normalizeHtmlBody(candCal?.body) ?? null,
    interviewer_calendar_body: normalizeHtmlBody(intCal?.body) ?? null,
    // Candidate email — full config snapshot
    candidate_email_enabled: Boolean(comm.send_email_to_candidate),
    candidate_ics_enabled: Boolean(comm.send_ics_to_candidate),
    candidate_email_from: normalizeFrom(candEmail),
    candidate_email_cc: normalizeRecipients(
      candEmail?.cc,
      candEmail?.cc_user_ids
    ),
    candidate_email_bcc: normalizeRecipients(
      candEmail?.bcc,
      candEmail?.bcc_user_ids
    ),
    candidate_email_subject: normalizeString(candEmail?.subject),
    candidate_email_body: normalizeHtmlBody(candEmail?.body) ?? null,
    candidate_email_attachments: normalizeAttachments(candEmail?.attachments),
    // Interviewer email — full config snapshot
    interviewer_email_enabled: Boolean(comm.send_email_to_interviewer),
    interviewer_ics_enabled: Boolean(comm.send_ics_to_interviewer),
    interviewer_email_from: normalizeFrom(intEmail),
    interviewer_email_cc: normalizeRecipients(
      intEmail?.cc,
      intEmail?.cc_user_ids
    ),
    interviewer_email_bcc: normalizeRecipients(
      intEmail?.bcc,
      intEmail?.bcc_user_ids
    ),
    interviewer_email_subject: normalizeString(intEmail?.subject),
    interviewer_email_body: normalizeHtmlBody(intEmail?.body) ?? null,
    interviewer_email_attachments: normalizeAttachments(intEmail?.attachments),
  };

  return snapshot;
}

export function snapshotFieldChanged(
  original: ChangeSnapshot,
  current: ChangeSnapshot,
  field: keyof ChangeSnapshot
): boolean {
  return (
    JSON.stringify(original[field] ?? null) !==
    JSON.stringify(current[field] ?? null)
  );
}
